import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { saSreChatApiRef } from '../api/SaSreChatApi';
import type {
  ChatMessage,
  ChatArtifact,
  ChatPanelMode,
  FunctionType,
  PortalContext,
  SreTask,
  SreExpert,
} from '../types';
import type {
  AgentInfo,
  ProcessingState,
  InsightItem,
  RecommendedAction,
  SreDomain,
} from '../types/agentTypes';
import { FUNCTION_TO_DOMAIN } from '../types/agentTypes';
import { DOMAIN_VISUALS } from '../constants/domainVisuals';

// ── エージェント生成ヘルパー ──────────────────────────
// activeFunction に応じて関連エージェントのデモ状態を生成
function buildAgentsForFunction(fn: FunctionType): readonly AgentInfo[] {
  const domain = FUNCTION_TO_DOMAIN[fn];
  const visual = DOMAIN_VISUALS[domain];

  // 各シナリオで関連するエージェントの組み合わせ
  const agentConfigs: Record<FunctionType, ReadonlyArray<{ domain: SreDomain; name: string }>> = {
    security_alert: [
      { domain: 'security', name: 'Security Agent' },
      { domain: 'log', name: 'Log Agent' },
      { domain: 'cost', name: 'Cost Agent' },
      { domain: 'iam', name: 'IAM Agent' },
    ],
    cost_analysis: [
      { domain: 'cost', name: 'Cost Agent' },
      { domain: 'performance', name: 'Performance Agent' },
      { domain: 'integrated', name: 'Report Agent' },
    ],
    performance_diagnosis: [
      { domain: 'performance', name: 'Performance Agent' },
      { domain: 'log', name: 'Log Agent' },
      { domain: 'cost', name: 'Cost Agent' },
    ],
    iam_audit: [
      { domain: 'iam', name: 'IAM Agent' },
      { domain: 'security', name: 'Security Agent' },
      { domain: 'log', name: 'Log Agent' },
    ],
    log_analysis: [
      { domain: 'log', name: 'Log Agent' },
      { domain: 'security', name: 'Security Agent' },
      { domain: 'iam', name: 'IAM Agent' },
    ],
    integrated_report: [
      { domain: 'security', name: 'Security Agent' },
      { domain: 'cost', name: 'Cost Agent' },
      { domain: 'performance', name: 'Performance Agent' },
      { domain: 'iam', name: 'IAM Agent' },
      { domain: 'log', name: 'Log Agent' },
      { domain: 'integrated', name: 'Report Agent' },
    ],
  };

  return agentConfigs[fn].map((cfg, i) => ({
    id: `agent-${cfg.domain}-${i}`,
    name: cfg.name,
    domain: cfg.domain,
    status: 'idle' as const,
  }));
}

// エージェントの段階的ステータス遷移をシミュレート
function simulateAgentProgress(
  agents: readonly AgentInfo[],
  progress: number,
): readonly AgentInfo[] {
  const step = 100 / agents.length;
  return agents.map((agent, i) => {
    const agentThreshold = step * i;
    const agentComplete = step * (i + 1);

    if (progress >= agentComplete) {
      return { ...agent, status: 'completed' as const };
    }
    if (progress >= agentThreshold) {
      return { ...agent, status: 'running' as const };
    }
    return { ...agent, status: 'idle' as const };
  });
}

// ── State ──────────────────────────────────────────────

interface ChatState {
  messages: ChatMessage[];
  artifacts: ChatArtifact[];
  panelMode: ChatPanelMode;
  isStreaming: boolean;
  activeFunction: FunctionType;
  activeTask: string | null;
  tasks: SreTask[];
  experts: SreExpert[];
  suggestions: string[];
  portalContext: PortalContext;
  unreadCount: number;
  sessionId: string | null;
  // Phase 1: エージェント協調
  agentStatuses: readonly AgentInfo[];
  processingState: ProcessingState;
  // Phase 2: ディスカッションステータス
  insights: readonly InsightItem[];
  recommendedActions: readonly RecommendedAction[];
  isDiscussionUpdating: boolean;
}

const initialState: ChatState = {
  messages: [],
  artifacts: [],
  panelMode: 'fab',
  isStreaming: false,
  activeFunction: 'security_alert',
  activeTask: null,
  tasks: [],
  experts: [],
  suggestions: [],
  portalContext: { pageType: 'other' },
  unreadCount: 0,
  sessionId: null,
  // Phase 1
  agentStatuses: [],
  processingState: { stage: 'idle' },
  // Phase 2
  insights: [],
  recommendedActions: [],
  isDiscussionUpdating: false,
};

// ── Actions ────────────────────────────────────────────

type ChatAction =
  | { type: 'ADD_MESSAGE'; payload: ChatMessage }
  | { type: 'ADD_ARTIFACT'; payload: ChatArtifact }
  | { type: 'SET_PANEL_MODE'; payload: ChatPanelMode }
  | { type: 'SET_STREAMING'; payload: boolean }
  | { type: 'SET_ACTIVE_FUNCTION'; payload: FunctionType }
  | { type: 'SET_ACTIVE_TASK'; payload: string | null }
  | { type: 'SET_TASKS'; payload: SreTask[] }
  | { type: 'SET_EXPERTS'; payload: SreExpert[] }
  | { type: 'TOGGLE_EXPERT'; payload: string }
  | { type: 'SET_SUGGESTIONS'; payload: string[] }
  | { type: 'SET_PORTAL_CONTEXT'; payload: PortalContext }
  | { type: 'CLEAR_UNREAD' }
  | { type: 'CLEAR_CHAT' }
  // Phase 1: エージェント協調
  | { type: 'SET_AGENT_STATUSES'; payload: readonly AgentInfo[] }
  | { type: 'SET_PROCESSING_STATE'; payload: ProcessingState }
  // Phase 2: ディスカッションステータス
  | { type: 'SET_INSIGHTS'; payload: readonly InsightItem[] }
  | { type: 'SET_RECOMMENDED_ACTIONS'; payload: readonly RecommendedAction[] }
  | { type: 'SET_DISCUSSION_UPDATING'; payload: boolean };

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, action.payload],
        unreadCount:
          state.panelMode === 'fab' && action.payload.sender !== 'user'
            ? state.unreadCount + 1
            : state.unreadCount,
      };
    case 'ADD_ARTIFACT':
      return {
        ...state,
        artifacts: [...state.artifacts, action.payload],
      };
    case 'SET_PANEL_MODE':
      return {
        ...state,
        panelMode: action.payload,
        unreadCount: action.payload !== 'fab' ? 0 : state.unreadCount,
      };
    case 'SET_STREAMING':
      return { ...state, isStreaming: action.payload };
    case 'SET_ACTIVE_FUNCTION':
      return { ...state, activeFunction: action.payload, activeTask: null };
    case 'SET_ACTIVE_TASK':
      return { ...state, activeTask: action.payload };
    case 'SET_TASKS':
      return { ...state, tasks: action.payload };
    case 'SET_EXPERTS':
      return { ...state, experts: action.payload };
    case 'TOGGLE_EXPERT':
      return {
        ...state,
        experts: state.experts.map(e =>
          e.id === action.payload ? { ...e, enabled: !e.enabled } : e,
        ),
      };
    case 'SET_SUGGESTIONS':
      return { ...state, suggestions: action.payload };
    case 'SET_PORTAL_CONTEXT':
      return { ...state, portalContext: action.payload };
    case 'CLEAR_UNREAD':
      return { ...state, unreadCount: 0 };
    case 'CLEAR_CHAT':
      return {
        ...state,
        messages: [],
        artifacts: [],
        agentStatuses: [],
        processingState: { stage: 'idle' },
        insights: [],
        recommendedActions: [],
      };
    // Phase 1
    case 'SET_AGENT_STATUSES':
      return { ...state, agentStatuses: action.payload };
    case 'SET_PROCESSING_STATE':
      return { ...state, processingState: action.payload };
    // Phase 2
    case 'SET_INSIGHTS':
      return { ...state, insights: action.payload };
    case 'SET_RECOMMENDED_ACTIONS':
      return { ...state, recommendedActions: action.payload };
    case 'SET_DISCUSSION_UPDATING':
      return { ...state, isDiscussionUpdating: action.payload };
    default:
      return state;
  }
}

// ── Context ────────────────────────────────────────────

interface ChatContextValue {
  state: ChatState;
  /** Send a user message and stream the agent response */
  sendMessage: (text: string, file?: File) => Promise<void>;
  /** Open the chat panel */
  openChat: () => void;
  /** Close the chat panel (back to FAB) */
  closeChat: () => void;
  /** Switch to full-screen chat */
  goFullscreen: () => void;
  /** Switch to compact panel */
  goPanel: () => void;
  /** Set the active SRE function */
  setFunction: (fn: FunctionType) => void;
  /** Set the active task */
  setTask: (taskId: string | null) => void;
  /** Toggle an expert on/off */
  toggleExpert: (expertId: string) => void;
  /** Update portal context (called by ContextBridge) */
  updatePortalContext: (ctx: PortalContext) => void;
  /** Load tasks and experts for current function */
  loadFunctionData: () => Promise<void>;
  /** Clear the chat history */
  clearChat: () => void;
  /** Start chat with a specific alert context */
  chatAboutAlert: (alertId: string, alertTitle: string) => void;
  /** Start chat with a specific entity context */
  chatAboutEntity: (entityName: string, entityKind: string) => void;
}

const ChatCtx = createContext<ChatContextValue | undefined>(undefined);

// ── Provider ───────────────────────────────────────────

interface ChatProviderProps {
  children: React.ReactNode;
  /** 初期表示モード。ルートベースのページでは 'fullscreen' を指定する */
  initialPanelMode?: ChatPanelMode;
}

export function ChatProvider({ children, initialPanelMode }: ChatProviderProps) {
  const api = useApi(saSreChatApiRef);
  const [state, dispatch] = useReducer(
    chatReducer,
    initialPanelMode
      ? { ...initialState, panelMode: initialPanelMode }
      : initialState,
  );

  const sendMessage = useCallback(
    async (text: string, file?: File) => {
      // Add user message
      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        sender: 'user',
        content: text,
        timestamp: new Date(),
        functionContext: state.activeFunction,
        taskContext: state.activeTask ?? undefined,
      };
      dispatch({ type: 'ADD_MESSAGE', payload: userMsg });

      // Phase 1: 即座に received 状態に遷移（Doherty Threshold: < 100ms）
      dispatch({ type: 'SET_PROCESSING_STATE', payload: { stage: 'received' } });
      dispatch({ type: 'SET_STREAMING', payload: true });

      // エージェントステータスを初期化
      const agents = buildAgentsForFunction(state.activeFunction);
      dispatch({ type: 'SET_AGENT_STATUSES', payload: agents });

      // クライアントに現在の機能タイプを伝達（フォールバック用）
      api.setCurrentFunction(state.activeFunction);

      // タイマー参照を保持（確実なクリーンアップのため）
      let processingTimerId: ReturnType<typeof setTimeout> | null = null;
      let progressTimerId: ReturnType<typeof setInterval> | null = null;

      const cleanupTimers = () => {
        if (processingTimerId !== null) {
          clearTimeout(processingTimerId);
          processingTimerId = null;
        }
        if (progressTimerId !== null) {
          clearInterval(progressTimerId);
          progressTimerId = null;
        }
      };

      try {
        const enabledExperts = state.experts
          .filter(e => e.enabled)
          .map(e => e.id);

        // processing 状態に遷移（400ms 後 — より速いフィードバック）
        processingTimerId = setTimeout(() => {
          dispatch({
            type: 'SET_PROCESSING_STATE',
            payload: { stage: 'processing', progress: 0, activeAgents: agents.map(a => a.id) },
          });
        }, 400);

        // 段階的進捗シミュレーション（エージェント連動）
        let progressValue = 0;
        progressTimerId = setInterval(() => {
          // 自然な進捗カーブ: 序盤は速く、終盤は遅くなる
          const remaining = 92 - progressValue;
          const increment = Math.max(2, remaining * 0.15 + Math.random() * 5);
          progressValue = Math.min(progressValue + increment, 92);

          dispatch({
            type: 'SET_PROCESSING_STATE',
            payload: {
              stage: 'processing',
              progress: progressValue,
              activeAgents: agents.filter((_, i) => {
                const step = 92 / agents.length;
                return progressValue >= step * i && progressValue < step * (i + 1);
              }).map(a => a.id),
            },
          });
          // エージェントステータスも連動更新
          dispatch({
            type: 'SET_AGENT_STATUSES',
            payload: simulateAgentProgress(agents, progressValue),
          });
        }, 600);

        await api.sendMessage(
          text,
          state.activeTask,
          enabledExperts,
          chunk => {
            if (chunk.type === 'message' && chunk.message) {
              dispatch({ type: 'ADD_MESSAGE', payload: chunk.message });
            }
            if (chunk.type === 'artifact' && chunk.artifact) {
              dispatch({ type: 'ADD_ARTIFACT', payload: chunk.artifact });
            }
          },
          file,
        );

        // タイマーを確実にクリア
        cleanupTimers();

        // 全エージェント完了
        dispatch({
          type: 'SET_AGENT_STATUSES',
          payload: agents.map(a => ({ ...a, status: 'completed' as const })),
        });

        // Phase 1: completed 状態（Peak-End Rule: ポジティブエンド）
        dispatch({
          type: 'SET_PROCESSING_STATE',
          payload: {
            stage: 'completed',
            deliverables: [
              { name: '分析レポート', detail: 'AI分析結果', iconType: 'doc' },
              { name: '推奨アクション', detail: '対応提案', iconType: 'bulb' },
            ],
          },
        });

        // Phase 2: ディスカッションステータスを更新
        dispatch({ type: 'SET_DISCUSSION_UPDATING', payload: true });
        const domain = FUNCTION_TO_DOMAIN[state.activeFunction];
        dispatch({
          type: 'SET_INSIGHTS',
          payload: [
            {
              agentName: agents[0]?.name ?? 'Agent',
              content: '分析を完了しました。詳細は成果物タブを確認してください。',
            },
          ],
        });
        dispatch({
          type: 'SET_RECOMMENDED_ACTIONS',
          payload: [
            {
              id: 'action-1',
              title: '詳細レポートを確認',
              domain,
              taskType: state.activeFunction,
            },
          ],
        });
        dispatch({ type: 'SET_DISCUSSION_UPDATING', payload: false });

        // Refresh suggestions after response
        const suggestions = await api.getSuggestions(state.activeTask);
        dispatch({ type: 'SET_SUGGESTIONS', payload: suggestions });

        // 5秒後に processing を idle に戻す
        setTimeout(() => {
          dispatch({ type: 'SET_PROCESSING_STATE', payload: { stage: 'idle' } });
        }, 5000);
      } catch (err) {
        // タイマーを確実にクリア
        cleanupTimers();

        // Phase 1: エラー状態
        dispatch({
          type: 'SET_PROCESSING_STATE',
          payload: {
            stage: 'error',
            error: {
              message: err instanceof Error ? err.message : String(err),
              reason: '通信エラーが発生しました',
            },
          },
        });
        dispatch({
          type: 'SET_AGENT_STATUSES',
          payload: agents.map(a =>
            a.status === 'running' ? { ...a, status: 'error' as const } : a,
          ),
        });
      } finally {
        cleanupTimers();
        dispatch({ type: 'SET_STREAMING', payload: false });
      }
    },
    [api, state.activeFunction, state.activeTask, state.experts],
  );

  const openChat = useCallback(() => {
    dispatch({ type: 'SET_PANEL_MODE', payload: 'panel' });
  }, []);

  const closeChat = useCallback(() => {
    dispatch({ type: 'SET_PANEL_MODE', payload: 'fab' });
  }, []);

  const goFullscreen = useCallback(() => {
    dispatch({ type: 'SET_PANEL_MODE', payload: 'fullscreen' });
  }, []);

  const goPanel = useCallback(() => {
    dispatch({ type: 'SET_PANEL_MODE', payload: 'panel' });
  }, []);

  const setFunction = useCallback((fn: FunctionType) => {
    dispatch({ type: 'SET_ACTIVE_FUNCTION', payload: fn });
  }, []);

  const setTask = useCallback((taskId: string | null) => {
    dispatch({ type: 'SET_ACTIVE_TASK', payload: taskId });
  }, []);

  const toggleExpert = useCallback((expertId: string) => {
    dispatch({ type: 'TOGGLE_EXPERT', payload: expertId });
  }, []);

  const updatePortalContext = useCallback((ctx: PortalContext) => {
    dispatch({ type: 'SET_PORTAL_CONTEXT', payload: ctx });
  }, []);

  const loadFunctionData = useCallback(async () => {
    const [tasks, experts] = await Promise.all([
      api.getTasks(state.activeFunction),
      api.getExperts(state.activeFunction),
    ]);
    dispatch({ type: 'SET_TASKS', payload: tasks });
    dispatch({ type: 'SET_EXPERTS', payload: experts });
  }, [api, state.activeFunction]);

  const clearChat = useCallback(() => {
    dispatch({ type: 'CLEAR_CHAT' });
  }, []);

  const chatAboutAlert = useCallback(
    (alertId: string, alertTitle: string) => {
      dispatch({ type: 'SET_ACTIVE_FUNCTION', payload: 'security_alert' });
      dispatch({ type: 'SET_PANEL_MODE', payload: 'panel' });
      dispatch({
        type: 'SET_PORTAL_CONTEXT',
        payload: { pageType: 'dashboard', dashboardDomain: 'security_alert', alertId },
      });
      // Inject alert context as system message
      const contextMsg: ChatMessage = {
        id: crypto.randomUUID(),
        sender: 'pmo',
        content: `**Alert Context**: ${alertTitle} (ID: ${alertId})\nこのアラートについて分析を開始します。`,
        timestamp: new Date(),
        functionContext: 'security_alert',
      };
      dispatch({ type: 'ADD_MESSAGE', payload: contextMsg });
    },
    [],
  );

  const chatAboutEntity = useCallback(
    (entityName: string, entityKind: string) => {
      dispatch({ type: 'SET_PANEL_MODE', payload: 'panel' });
      dispatch({
        type: 'SET_PORTAL_CONTEXT',
        payload: { pageType: 'catalog', entityName, entityKind },
      });
      const contextMsg: ChatMessage = {
        id: crypto.randomUUID(),
        sender: 'pmo',
        content: `**Service Context**: ${entityName} (${entityKind})\nこのサービスについてお手伝いします。何を調査しますか？`,
        timestamp: new Date(),
      };
      dispatch({ type: 'ADD_MESSAGE', payload: contextMsg });
    },
    [],
  );

  const value = useMemo<ChatContextValue>(
    () => ({
      state,
      sendMessage,
      openChat,
      closeChat,
      goFullscreen,
      goPanel,
      setFunction,
      setTask,
      toggleExpert,
      updatePortalContext,
      loadFunctionData,
      clearChat,
      chatAboutAlert,
      chatAboutEntity,
    }),
    [
      state,
      sendMessage,
      openChat,
      closeChat,
      goFullscreen,
      goPanel,
      setFunction,
      setTask,
      toggleExpert,
      updatePortalContext,
      loadFunctionData,
      clearChat,
      chatAboutAlert,
      chatAboutEntity,
    ],
  );

  return <ChatCtx.Provider value={value}>{children}</ChatCtx.Provider>;
}

/** Hook to access the chat context. Throws if used outside ChatProvider. */
export function useChat(): ChatContextValue {
  const ctx = useContext(ChatCtx);
  if (!ctx) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return ctx;
}
